import { useMemo, useState } from 'react';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { Sheet, SheetGroup } from '@/components/ui/Sheet';
import { useToast } from '@/components/ui/Toast';
import { groupRoundsApi, guestPlayersApi, roundsApi } from '@/lib/api/endpoints';
import { useCourse, useFriends, useGuestPlayers } from '@/lib/api/queries';
import type {
  GroupContest,
  GroupFormat,
  GroupHandicapMode,
  GroupScoring,
  NewParticipantInput,
  TeamFormat,
  TeamId,
} from '@/lib/api/types';
import { MAX_FLIGHT_PLAYERS } from '@/lib/api/types';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { fmtDecimal, fmtOrdinal } from '@/lib/format/number';
import { t, tx } from '@/lib/i18n';
import { courseHandicapOf, courseSnapshotFor, strokesReceivedTotal } from './groupMath';
import styles from './Record.module.css';
import { CloseIcon } from '@/components/icons';

/**
 * Passo 3 — "Quem jogou com você" e em que formato (canvas 1r).
 *
 * Duas rotas para adicionar, e a segunda é a que faz o recurso funcionar no dia 1: escolher da
 * lista de amigos, ou digitar nome e handicap de quem não usa o app. **O handicap é opcional** —
 * sem ele não há net, e a tela diz isso em vez de bloquear.
 */

/** Um jogador já posto no flight, antes de a volta existir. */
interface DraftPlayer {
  key: string;
  kind: 'user' | 'guest';
  userId?: string;
  guestPlayerId?: string;
  displayName: string;
  handicapIndex: number | null;
  team: TeamId;
}

export interface StartedRound {
  roundId: string;
  groupRoundId: string | null;
}

export function StepPlayers({
  config,
  header,
  onStarted,
}: {
  config: {
    courseId: string;
    teeBoxName: string;
    teeBoxNameBack: string | null;
    holesPlayed: 9 | 18;
  };
  header: React.ReactNode;
  onStarted: (started: StartedRound) => void;
}) {
  const user = useCurrentUser();
  const course = useCourse(config.courseId);
  const toast = useToast();

  const [players, setPlayers] = useState<DraftPlayer[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [contest, setContest] = useState<GroupContest>('individual');
  const [teamFormat, setTeamFormat] = useState<TeamFormat>('best_ball');
  const [scoring, setScoring] = useState<GroupScoring>('stroke');
  const [handicapMode, setHandicapMode] = useState<GroupHandicapMode>('net');
  const [ownerTeam, setOwnerTeam] = useState<TeamId>('A');
  const [starting, setStarting] = useState(false);
  const isDesktop = useIsDesktop();

  // O mesmo snapshot que o servidor vai congelar na volta — média dos tees numa volta dupla,
  // par dos nove primeiros numa volta de 9. Sem isso, o número aqui e o do scorecard divergiriam.
  const snapshot = course.data
    ? courseSnapshotFor(course.data, config.teeBoxName, config.teeBoxNameBack, config.holesPlayed)
    : null;

  /** Tacadas que cada um recebe no tee escolhido — o número que o 1r imprime sob o nome. */
  const strokesFor = (handicapIndex: number | null): number | null => {
    if (!snapshot) return null;
    const ch = courseHandicapOf(
      handicapIndex,
      snapshot.slopeRating,
      snapshot.courseRating,
      snapshot.par,
    );
    return ch === null ? null : strokesReceivedTotal(ch);
  };

  const isSolo = players.length === 0;
  const full = players.length + 1 >= MAX_FLIGHT_PLAYERS;
  const flightSize = players.length + 1;
  // Duplas são 2×2 — o canvas só desenha esse caso, e best ball não faz sentido fora dele.
  const canPlayTeams = flightSize === MAX_FLIGHT_PLAYERS;
  const isTeams = contest === 'teams' && canPlayTeams;
  // "Match play só existe em duplas ou entre dois jogadores." Fora disso a cápsula nem aparece.
  const canPlayMatch = isTeams || flightSize === 2;

  const teamOf = (key: string): TeamId =>
    key === 'you' ? ownerTeam : (players.find((p) => p.key === key)?.team ?? 'B');

  const membersOf = (team: TeamId) => [
    ...(ownerTeam === team
      ? [{ key: 'you', displayName: t('record.you'), handicapIndex: user.handicapIndex }]
      : []),
    ...players.filter((p) => p.team === team),
  ];

  const setTeam = (key: string, team: TeamId) => {
    if (key === 'you') setOwnerTeam(team);
    else setPlayers((prev) => prev.map((p) => (p.key === key ? { ...p, team } : p)));
  };

  /*
   * O celular cicla com um toque porque o alvo é um só; o desktop escolhe com dois botões
   * (canvas 1x: "com espaço, dois botões por linha são mais claros que um alvo que cicla").
   * As duas portas mexem no mesmo estado, então o resumo dos times reage igual.
   */
  const swapTeam = (key: string) => setTeam(key, teamOf(key) === 'A' ? 'B' : 'A');

  /** Soma crua dos handicaps do time — no scramble ela é a regra, no best ball só compara. */
  const teamHandicap = (team: TeamId): number | null => {
    const values = membersOf(team)
      .map((m) => m.handicapIndex)
      .filter((v): v is number => v !== null);
    return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
  };

  const gapBetweenTeams = (() => {
    const a = teamHandicap('A');
    const b = teamHandicap('B');
    if (a === null || b === null) return null;
    const diff = Math.abs(a - b);
    return diff >= 5 ? { heavier: a > b ? 'A' : 'B', diff } : null;
  })();

  const start = async () => {
    setStarting(true);
    try {
      if (isSolo) {
        // Sem ninguém no flight, a volta é a de sempre — mesmo endpoint, mesmo scorecard.
        const round = await roundsApi.start({
          courseId: config.courseId,
          teeBoxName: config.teeBoxName,
          ...(config.teeBoxNameBack ? { teeBoxNameBack: config.teeBoxNameBack } : {}),
          holesPlayed: config.holesPlayed,
        });
        onStarted({ roundId: round.id, groupRoundId: null });
        return;
      }

      const format: GroupFormat = isTeams
        ? { contest: 'teams', teamFormat, scoring: canPlayMatch ? scoring : 'stroke', handicapMode }
        : { contest: 'individual', scoring: canPlayMatch ? scoring : normalise(scoring), handicapMode };
      const group = await groupRoundsApi.create({
        courseId: config.courseId,
        teeBoxName: config.teeBoxName,
        ...(config.teeBoxNameBack ? { teeBoxNameBack: config.teeBoxNameBack } : {}),
        holesPlayed: config.holesPlayed,
        format,
        players: players.map((p) => toParticipantInput(p, isTeams)),
        ...(isTeams ? { ownerTeam } : {}),
      });
      onStarted({ roundId: group.roundId, groupRoundId: group.id });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('record.startFailed'));
    } finally {
      setStarting(false);
    }
  };

  const teamPicker = (key: string, label: string) =>
    isTeams && isDesktop ? (
      <span className={styles.teamPicker} role="group" aria-label={t('record.teamOf', { name: label })}>
        {(['A', 'B'] as TeamId[]).map((team) => (
          <button
            key={team}
            type="button"
            className={cx(styles.teamPick, teamOf(key) === team && styles.teamPickOn)}
            aria-pressed={teamOf(key) === team}
            onClick={() => setTeam(key, team)}
          >
            {team}
          </button>
        ))}
      </span>
    ) : null;

  const flight = (
    <div className={styles.flight}>
      <PlayerRow
        you
        displayName={user.displayName}
        handicapIndex={user.handicapIndex}
        strokes={strokesFor(user.handicapIndex)}
        aside={teamPicker('you', t('record.youLower'))}
      />
      {players.map((p) => (
        <PlayerRow
          key={p.key}
          displayName={p.displayName}
          handicapIndex={p.handicapIndex}
          strokes={strokesFor(p.handicapIndex)}
          guest={p.kind === 'guest'}
          aside={teamPicker(p.key, p.displayName)}
          onRemove={() => setPlayers((prev) => prev.filter((x) => x.key !== p.key))}
        />
      ))}

      {!full && (
        <button type="button" className={styles.addPlayer} onClick={() => setSheetOpen(true)}>
          <span className={styles.addPlayerPlus} aria-hidden="true">
            +
          </span>
          <span>
            <span className={styles.addPlayerLabel}>
              {t('record.addNthPlayer', { ordinal: fmtOrdinal(players.length + 2) })}
            </span>
            <span className={styles.addPlayerSub}>{t('record.addPlayerSub')}</span>
          </span>
        </button>
      )}
    </div>
  );

  /*
   * O aviso de desigualdade e o resumo dos times andam juntos: o desktop os põe sob o flight,
   * onde a troca acontece, para que mudar de dupla e ver o efeito sejam o mesmo olhar.
   */
  const gapWarning = gapBetweenTeams ? (
    <p className={styles.formatWarn}>
      {tx('record.gapWarning', {
        team: gapBetweenTeams.heavier,
        amount: (
          <strong>{t('record.gapAmount', { value: fmtDecimal(gapBetweenTeams.diff) })}</strong>
        ),
      })}{' '}
      {teamFormat === 'best_ball' ? t('record.gapBestBall') : t('record.gapScramble')}
    </p>
  ) : null;

  /** O resumo compacto do desktop: uma linha por dupla, com a soma ao lado. */
  const teamSummary = (
    <div className={styles.teamSummary}>
      {(['A', 'B'] as TeamId[]).map((team) => (
        <div key={team} className={styles.teamSummaryRow}>
          <span className={styles.teamBadge}>{team}</span>
          <span className={styles.teamSummaryNames}>
            {membersOf(team)
              .map((m) => m.displayName)
              .join(' + ')}
          </span>
          <span className={styles.teamSum}>{fmtDecimal(teamHandicap(team))}</span>
        </div>
      ))}
    </div>
  );

  const teamBlocks = (
    <>
      <div className={styles.groupLabel}>
        {t('record.teams')}
        <span className={styles.groupLabelAside}>{t('record.teamsAside')}</span>
      </div>
      <p className={styles.playersHint}>{t('record.tapToSwap')}</p>
      {(['A', 'B'] as TeamId[]).map((team) => (
        <div key={team} className={styles.teamBlock}>
          <div className={styles.teamHead}>
            <span className={styles.teamBadge}>{team}</span>
            <span className={styles.teamName}>{t('record.teamName', { team })}</span>
            <span className={styles.teamSum}>{fmtDecimal(teamHandicap(team))}</span>
          </div>
          {membersOf(team).map((m) => (
            <button
              key={'key' in m ? m.key : 'you'}
              type="button"
              className={styles.teamMember}
              onClick={() => swapTeam('key' in m ? m.key : 'you')}
            >
              <Avatar
                name={m.displayName}
                size={30}
                variant={('key' in m ? m.key : '') === 'you' ? 'gold' : 'green'}
              />
              <span className={styles.teamMemberBody}>
                <span className={styles.playerName}>{m.displayName}</span>
                <span className={styles.playerMeta}>
                  {m.handicapIndex === null
                    ? t('sidebar.noHandicap')
                    : t('friends.handicap', { value: fmtDecimal(m.handicapIndex) })}
                </span>
              </span>
              <span className={styles.teamSwap}>{t('record.swap')}</span>
            </button>
          ))}
        </div>
      ))}
      {gapWarning}
    </>
  );

  /*
   * Os eixos de formato só existem quando há com quem disputar. Sozinho, "gross ou net" não
   * decide nada — não há segundo número para comparar.
   */
  /*
   * Os nomes dos formatos ficam **sem glossário na tela** (canvas 1r): as cinco frases que
   * explicavam best ball, scramble, stroke, match e net/gross desceram para a legenda do
   * canvas. O que fala com o usuário num estado específico continua — o aviso de duplas
   * desiguais, logo abaixo, é o exemplo: ele só aparece quando os times realmente desandam.
   */
  const formatGroups = isSolo ? null : (
    <>
      <div className={styles.groupLabel}>{t('record.contest')}</div>
      <div className={styles.pills}>
        <Pill selected={!isTeams} onClick={() => setContest('individual')}>
          {t('record.individual')}
        </Pill>
        {/* Duplas são 2×2; num flight de dois ou três a cápsula nem aparece. */}
        {canPlayTeams && (
          <Pill selected={isTeams} onClick={() => setContest('teams')}>
            {t('record.pairs')}
          </Pill>
        )}
      </div>

      {isTeams && (
        <>
          <div className={styles.groupLabel}>{t('record.teamFormat')}</div>
          <div className={styles.pills}>
            <Pill selected={teamFormat === 'best_ball'} onClick={() => setTeamFormat('best_ball')}>
              {t('record.bestBall')}
            </Pill>
            <Pill selected={teamFormat === 'scramble'} onClick={() => setTeamFormat('scramble')}>
              {t('record.scramble')}
            </Pill>
          </div>
        </>
      )}

      <div className={styles.groupLabel}>{t('record.scoring')}</div>
      <div className={styles.pills}>
        <Pill selected={scoring === 'stroke'} onClick={() => setScoring('stroke')}>
          {t('record.strokePlay')}
        </Pill>
        <Pill selected={scoring === 'stableford'} onClick={() => setScoring('stableford')}>
          {t('record.stableford')}
        </Pill>
        {/*
          Match play **não aparece** fora de duplas ou de um flight de dois — é confronto
          direto, e o canvas é explícito: "mostrá-lo desabilitado seria oferecer o que a
          tela não faz".
        */}
        {canPlayMatch && (
          <Pill selected={scoring === 'match'} onClick={() => setScoring('match')}>
            {t('record.matchPlay')}
          </Pill>
        )}
      </div>

      <div className={styles.groupLabel}>{t('record.handicap')}</div>
      <div className={styles.pills}>
        <Pill selected={handicapMode === 'gross'} onClick={() => setHandicapMode('gross')}>
          {t('record.gross')}
        </Pill>
        <Pill selected={handicapMode === 'net'} onClick={() => setHandicapMode('net')}>
          {t('record.net')}
        </Pill>
      </div>


      {/* No desktop os times moram na coluna do flight; aqui eles seguem o formato. */}
      {isTeams && !isDesktop && teamBlocks}
    </>
  );

  const startButton = (
    <div className={styles.startAction}>
      <Button block size="lg" busy={starting} onClick={() => void start()}>
        {t('record.startScorecard')}
      </Button>
    </div>
  );

  return (
    <>
      {header}
      <div className={styles.body}>
        <h1 className={styles.playersTitle}>
          {isDesktop ? t('record.whoPlayedQuestion') : t('record.whoPlayed')}
        </h1>
        {isDesktop && course.data ? (
          /* No desktop o contexto da volta vem junto: as duas decisões desta tela dependem
             do campo e do tee, e voltar um passo só para conferir seria trabalho à toa. */
          <p className={styles.playersHint}>
            {t('record.roundContext', {
              course: course.data.name,
              holes: config.holesPlayed,
              tee: config.teeBoxName,
            })}
          </p>
        ) : (
          <p className={styles.playersHint}>
            {t('record.flightMax', { max: MAX_FLIGHT_PLAYERS })}
          </p>
        )}

        {/*
          Jogadores e formato lado a lado (canvas 1x): no celular o jogador escolhe o formato
          antes de ver quem está no flight; aqui as duas decisões se informam, e trocar de
          duplas reordena os times na coluna da esquerda na hora.
        */}
        <div className={styles.playersLayout}>
          <div className={styles.playersCol}>
            {isDesktop && (
              <div className={styles.flightBar}>
                <span className={styles.flightBarCount}>
                  {t('record.flightCount', { size: flightSize, max: MAX_FLIGHT_PLAYERS })}
                </span>
              </div>
            )}
            {flight}
            {isTeams && isDesktop && (
              <>
                {teamSummary}
                {gapWarning}
              </>
            )}
          </div>

          <div className={styles.formatCol}>
            {formatGroups}
            {startButton}
          </div>
        </div>
      </div>

      <AddPlayerSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        alreadyIn={players}
        strokesFor={strokesFor}
        onAdd={(player) => {
          // Entra no time oposto ao seu por padrão: é o arranjo que a maioria quer, e trocar é
          // um toque. Começar todos no mesmo time obrigaria a arrumar o flight inteiro à mão.
          setPlayers((prev) => [
            ...prev,
            { ...player, team: prev.length === 0 ? oppositeOf(ownerTeam) : nextTeam(prev, ownerTeam) },
          ]);
          setSheetOpen(false);
        }}
      />
    </>
  );
}

function PlayerRow({
  displayName,
  handicapIndex,
  strokes,
  you,
  guest,
  aside,
  onRemove,
}: {
  displayName: string;
  handicapIndex: number | null;
  strokes: number | null;
  you?: boolean;
  guest?: boolean;
  /** O seletor A/B do desktop. Nulo no celular, onde a troca é o toque na linha do time. */
  aside?: React.ReactNode;
  onRemove?: () => void;
}) {
  return (
    // A faixa dourada + avatar preenchido marcando "você" — a mesma dupla do scorecard (1s).
    <div className={cx(styles.playerRow, you && styles.playerRowYou)}>
      <Avatar name={displayName} size={36} variant={you ? 'gold' : 'green'} />
      <div className={styles.playerBody}>
        <div className={styles.playerName}>
          {you ? t('record.you') : displayName}
          {guest && <span className={styles.playerTag}>{t('record.noAccount')}</span>}
        </div>
        <div className={styles.playerMeta}>{describePlayer(handicapIndex, strokes)}</div>
      </div>
      {aside}
      {onRemove && (
        <button
          type="button"
          className={styles.playerRemove}
          onClick={onRemove}
          aria-label={t('record.removeFromFlight', { name: displayName })}
        >
          <CloseIcon size={18} />
        </button>
      )}
    </div>
  );
}

/** "Handicap 8,4 · recebe 8 tacadas" — e o que dá para dizer de quem não tem índice. */
function describePlayer(handicapIndex: number | null, strokes: number | null): string {
  if (handicapIndex === null) return t('sidebar.noHandicap');
  const base = t('friends.handicap', { value: fmtDecimal(handicapIndex) });
  if (strokes === null) return base;
  return t('record.playerReceives', { base, count: strokes });
}

function AddPlayerSheet({
  open,
  onClose,
  alreadyIn,
  strokesFor,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  alreadyIn: DraftPlayer[];
  strokesFor: (handicapIndex: number | null) => number | null;
  onAdd: (player: Omit<DraftPlayer, 'team'>) => void;
}) {
  const friends = useFriends();
  const guests = useGuestPlayers();
  const toast = useToast();

  const [tab, setTab] = useState<'friends' | 'name'>('friends');
  const [name, setName] = useState('');
  const [handicap, setHandicap] = useState('');
  const [save, setSave] = useState(true);
  const [working, setWorking] = useState(false);

  const takenUserIds = useMemo(
    () => new Set(alreadyIn.map((p) => p.userId).filter(Boolean)),
    [alreadyIn],
  );
  const takenGuestIds = useMemo(
    () => new Set(alreadyIn.map((p) => p.guestPlayerId).filter(Boolean)),
    [alreadyIn],
  );

  const parsedHandicap = parseHandicap(handicap);
  const nameOk = name.trim().length > 0;
  const handicapOk = handicap.trim() === '' || parsedHandicap !== null;

  const addTyped = async () => {
    if (!nameOk || !handicapOk) return;
    setWorking(true);
    try {
      const displayName = name.trim();
      // "Salvar para as próximas" é opcional: sem ele o convidado vale só para esta volta e não
      // deixa rastro na lista de ninguém.
      const saved = save
        ? await guestPlayersApi.create({ displayName, handicapIndex: parsedHandicap })
        : null;
      onAdd({
        key: saved?.id ?? `typed-${displayName}-${Date.now()}`,
        kind: 'guest',
        guestPlayerId: saved?.id,
        displayName,
        handicapIndex: parsedHandicap,
      });
      setName('');
      setHandicap('');
      if (saved) void guests.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('record.addFailed'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('record.newPlayer')}>
      <div className={styles.pills} style={{ marginBottom: '1rem' }}>
        <Pill selected={tab === 'friends'} onClick={() => setTab('friends')}>
          {t('friends.title')}
        </Pill>
        <Pill selected={tab === 'name'} onClick={() => setTab('name')}>
          {t('record.justName')}
        </Pill>
      </div>

      {tab === 'friends' ? (
        <div className={styles.sheetList}>
          {(friends.data ?? [])
            .filter((f) => !takenUserIds.has(f.id))
            .map((f) => (
              <button
                key={f.id}
                type="button"
                className={styles.sheetRow}
                onClick={() =>
                  onAdd({
                    key: f.id,
                    kind: 'user',
                    userId: f.id,
                    displayName: f.displayName,
                    handicapIndex: f.handicapIndex,
                  })
                }
              >
                <Avatar name={f.displayName} size={34} variant="green" />
                <span className={styles.sheetRowBody}>
                  <span className={styles.playerName}>{f.displayName}</span>
                  <span className={styles.playerMeta}>
                    {describePlayer(f.handicapIndex, strokesFor(f.handicapIndex))}
                  </span>
                </span>
              </button>
            ))}

          {(guests.data ?? [])
            .filter((g) => !takenGuestIds.has(g.id))
            .map((g) => (
              <button
                key={g.id}
                type="button"
                className={styles.sheetRow}
                onClick={() =>
                  onAdd({
                    key: g.id,
                    kind: 'guest',
                    guestPlayerId: g.id,
                    displayName: g.displayName,
                    handicapIndex: g.handicapIndex,
                  })
                }
              >
                <Avatar name={g.displayName} size={34} variant="green" />
                <span className={styles.sheetRowBody}>
                  <span className={styles.playerName}>
                    {g.displayName}
                    <span className={styles.playerTag}>{t('record.noAccount')}</span>
                  </span>
                  <span className={styles.playerMeta}>
                    {describePlayer(g.handicapIndex, strokesFor(g.handicapIndex))}
                  </span>
                </span>
              </button>
            ))}

          {(friends.data ?? []).length === 0 && (guests.data ?? []).length === 0 && (
            <p className={styles.formatNote}>{t('record.noFriendsYet')}</p>
          )}
        </div>
      ) : (
        <>
          <SheetGroup label={t('record.name')}>
            <Field
              label={t('record.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder={t('record.namePlaceholder')}
            />
          </SheetGroup>
          <SheetGroup label={t('record.handicapIndex')}>
            <Field
              label={t('record.handicapIndex')}
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              inputMode="decimal"
              placeholder={t('record.handicapPlaceholder')}
              hint={
                parsedHandicap !== null
                  ? t('record.receivesOnTee', { count: strokesFor(parsedHandicap) ?? 0 })
                  : t('record.handicapOptional')
              }
              error={handicapOk ? null : t('record.handicapRange')}
            />
          </SheetGroup>

          <label className={styles.saveGuest}>
            <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} />
            <span>
              <span className={styles.saveGuestLabel}>{t('record.saveGuest')}</span>
              <span className={styles.saveGuestSub}>{t('record.saveGuestSub')}</span>
            </span>
          </label>

          <Button
            block
            size="lg"
            busy={working}
            disabled={!nameOk || !handicapOk}
            onClick={() => void addTyped()}
          >
            {t('record.addToFlight')}
          </Button>
        </>
      )}
    </Sheet>
  );
}

/** Aceita vírgula, que é como se escreve handicap em português. */
function parseHandicap(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < -10 || value > 54) return null;
  return value;
}

function toParticipantInput(player: DraftPlayer, isTeams: boolean): NewParticipantInput {
  const team = isTeams ? { team: player.team } : {};
  if (player.kind === 'user') return { kind: 'user', userId: player.userId, ...team };
  if (player.guestPlayerId) {
    return { kind: 'guest', guestPlayerId: player.guestPlayerId, ...team };
  }
  return {
    kind: 'guest',
    displayName: player.displayName,
    handicapIndex: player.handicapIndex,
    ...team,
  };
}

function oppositeOf(team: TeamId): TeamId {
  return team === 'A' ? 'B' : 'A';
}

/** Preenche A e B alternadamente, mantendo as duplas equilibradas em número. */
function nextTeam(current: { team: TeamId }[], ownerTeam: TeamId): TeamId {
  const inA = current.filter((p) => p.team === 'A').length + (ownerTeam === 'A' ? 1 : 0);
  const inB = current.filter((p) => p.team === 'B').length + (ownerTeam === 'B' ? 1 : 0);
  return inA <= inB ? 'A' : 'B';
}

/** Match play deixou de ser possível (o flight mudou de tamanho) — cai para stroke play. */
function normalise(scoring: GroupScoring): GroupScoring {
  return scoring === 'match' ? 'stroke' : scoring;
}
